import React, { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  IconButton,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const CollapsibleContactField = ({
  formData,
  onChange,
  registry,
  schema,
  uiSchema,
  idSchema,
  errorSchema,
  rawErrors,
  formContext,
}) => {
  const { SchemaField } = registry.fields;
  const contacts = Array.isArray(formData) ? formData : [];
  const [expandedIndex, setExpandedIndex] = useState(null);

  const formSubmissionDetails = formContext?.formData?.formSubmissionDetails;
  const defaultAddedRef = useRef(false);

  // Add default contact when formSubmissionDetails is present
  useEffect(() => {
    if (
      !defaultAddedRef.current &&
      formSubmissionDetails &&
      Object.keys(formSubmissionDetails).length > 0 &&
      contacts.length === 0
    ) {
      const defaultContact = {
        contactFirstName: formSubmissionDetails.fillersFirstName || "",
        contactLastName: formSubmissionDetails.fillersLastName || "",
        contactPhoneNumber: formSubmissionDetails.fillersPhone || "",
        contactEmail: formSubmissionDetails.email || "",
        contactRoles: ["primary"],
        contactCity: formSubmissionDetails.city || "",
        contactRelationship: formSubmissionDetails.fillerIdentity || "",
      };
      onChange([defaultContact]);
      setExpandedIndex(0);
      defaultAddedRef.current = true;
    }
  }, [formSubmissionDetails, contacts.length, onChange]);

  useEffect(() => {
    if (errorSchema && Object.keys(errorSchema).length > 0) {
      const errorIndex = Object.keys(errorSchema)
        .map((key) => parseInt(key, 10))
        .find((index) => errorSchema[index]);
      if (errorIndex !== undefined && errorIndex !== expandedIndex) {
        setExpandedIndex(errorIndex);
      }
    }
  }, [errorSchema, expandedIndex]);

  const handleAdd = () => {
    const newContacts = [...contacts, {}];
    onChange(newContacts);
    setExpandedIndex(newContacts.length - 1);
  };

  const handleRemove = (index) => {
    const updated = contacts.filter((_, i) => i !== index);
    onChange(updated);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleChange = (index, value) => {
    const updated = [...contacts];
    updated[index] = value;
    onChange(updated);
  };

  const handleAccordionToggle = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const handleFocus = (id) => {
    console.log(`Field focused: ${id}`);
  };

  const handleBlur = (id, value) => {
    console.log(`Field blurred: ${id}, value: ${value}`);
  };

  return (
    <>
      {contacts.map((contact, index) => (
        <Accordion
          key={index}
          expanded={expandedIndex === index}
          onChange={() => handleAccordionToggle(index)}
          sx={{
            mb: 2,
            "&.MuiAccordion-root": {
              boxShadow: "none",
              backgroundColor: "transparent",
              borderRadius: 0,
            },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                "&.Mui-expanded": { minHeight: "unset" },
                "& .MuiAccordionSummary-content.Mui-expanded": { margin: 0 },
              }}
            >
              <Typography variant="h5" color="default.main">
                {contact?.contactFirstName
                  ? `${contact.contactFirstName} ${
                      contact.contactLastName || ""
                    }`
                  : `Contact ${index + 1}`}
              </Typography>
            </AccordionSummary>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(index);
              }}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
          <AccordionDetails>
            <SchemaField
              name={`${index}`}
              schema={schema.items}
              uiSchema={uiSchema?.items || {}}
              formData={contact}
              onChange={(val) => handleChange(index, val)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              registry={registry}
              idSchema={idSchema?.[index] || {}}
              errorSchema={errorSchema?.[index] || {}}
              rawErrors={
                rawErrors?.filter((err) =>
                  err.property?.startsWith(`[${index}]`)
                ) || []
              }
            />
          </AccordionDetails>
        </Accordion>
      ))}
      <Button
        variant="outlined"
        onClick={handleAdd}
        startIcon={<AddIcon />}
        sx={{ ml: "auto" }}
      >
        Add Contact
      </Button>
    </>
  );
};

export default CollapsibleContactField;
